'use client';

import * as React from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Modal } from '../primitives/Modal';
import { ConfirmBody } from './ConfirmBody';

export interface ConfirmOptions {
  title?: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  /* Matches the shape of the imperative confirmDialog's options.
     Previously useConfirm hardcoded "Cancel" while confirmDialog
     accepted a cancelLabel prop — API drift between two surfaces
     that should behave identically. Default stays "Cancel". */
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (value: boolean) => void;
  /* Monotonic id set at enqueue time. Used as the Modal's React
     key so consecutive queued dialogs REMOUNT the Modal between
     requests. Without the key, the Modal's useEffect([open]) never
     re-fires across a queue (open stays true), so the focus trap
     keeps the first request's `previouslyFocused` snapshot and
     restores focus to the wrong trigger when a queue of dialogs
     triggered from different elements drains. */
  id: number;
}

/* Monotonic counter for ConfirmRequest.id. Intentionally module-
   global: React keys only need uniqueness per-parent, but storing
   the counter per-hook via useRef would produce colliding ids
   across two hook instances (both would start at 1 and issue the
   same id to different requests). A module-global counter never
   collides. Read/write is synchronous inside confirm() so
   there's no race even under concurrent state updates. */
let _requestSeq = 0;

export interface UseConfirmReturn {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  node: ReactElement | null;
}

/**
 * Declarative confirmation-dialog hook. Returns `confirm()` (an
 * async API that opens a Modal and resolves with a boolean when
 * the user dismisses) and `node` (render into the tree).
 *
 * Concurrency: calling `confirm()` while a dialog is already open
 * enqueues the new request FIFO. Each queued request unmounts +
 * remounts the underlying Modal (per-id React key) so focus-trap
 * restoration targets the correct trigger per-entry.
 *
 * Stacking: the Modal opened by this hook shares
 * `src/kit/internal/modalStack` with `<Modal>` and the imperative
 * `confirmDialog` / `alertDialog`. Only the topmost Modal on the
 * stack handles Escape and the focus-trap. An `alertDialog()`
 * fired WHILE a useConfirm Modal is open therefore steals Escape
 * from the useConfirm Modal until the alert closes. This is the
 * intended "one Escape dismisses the topmost" behaviour but is
 * worth knowing if a consumer coordinates both surfaces in the
 * same flow.
 */

/* Queue concurrent confirm() calls. The previous version stored a
   single state object, so `Promise.all([confirm(a), confirm(b)])`
   would silently drop the first promise when the second setState
   overwrote it. Now: one visible state at a time + a FIFO of
   pending requests; each resolves in registration order. Matches
   the imperative surface in src/kit/dialogs/imperative.tsx. */
export function useConfirm(): UseConfirmReturn {
  const [active, setActive] = React.useState<ConfirmRequest | null>(null);
  /* activeRef mirrors `active` but is updated synchronously inside
     confirm() BEFORE React commits. Without it, two confirm() calls
     inside the same tick both see `active === null` through their
     captured closures, both take the else-branch, and the second
     setActive(reqY) overwrites the first so reqX never resolves.
     Promise.all([confirm(X), confirm(Y)]) is the canonical repro. */
  const activeRef = React.useRef<ConfirmRequest | null>(null);
  const queueRef = React.useRef<ConfirmRequest[]>([]);

  const activate = React.useCallback((req: ConfirmRequest) => {
    activeRef.current = req;
    setActive(req);
  }, []);

  const finish = React.useCallback((req: ConfirmRequest, value: boolean) => {
    /* Guard against double-close: pressing Cancel then Escape in the
       same tick (or any two activation paths firing before React
       commits) calls finish(req, …) twice. Without this guard the
       first call resolves req + shifts the queue; the second call
       re-resolves the same req (no-op, Promises are settle-once)
       but ALSO shifts again — orphaning the second queue entry
       whose promise would never settle. Identity-check req against
       the current activeRef to skip the stale call entirely. */
    if (activeRef.current !== req) return;
    req.resolve(value);
    const next = queueRef.current.shift() ?? null;
    activeRef.current = next;
    setActive(next);
  }, []);

  const confirm = React.useCallback(
    (opts: ConfirmOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        _requestSeq += 1;
        const req: ConfirmRequest = { ...opts, resolve, id: _requestSeq };
        /* Check the ref, NOT the state. The ref reflects any
         synchronous activate() from an earlier confirm() call in
         the same tick. */
        if (activeRef.current) {
          queueRef.current.push(req);
        } else {
          activate(req);
        }
      });
    },
    [activate],
  );

  /* Cleanup on unmount — resolve every pending promise so the hook
     owner unmounting (route change, parent re-render that drops the
     subtree, error boundary catch) doesn't leave consumer code
     awaiting forever. Resolve with `false` to match the user-cancelled
     semantics — adopters already handle that branch.

     Known limitation (StrictMode dev only): React 18 StrictMode runs
     this cleanup once between the synthetic mount/unmount pair. If a
     consumer opens a confirm from a mount effect (rare — usually
     confirms fire from event handlers), the first synthetic cleanup
     will resolve that promise with `false` prematurely; the second
     mount produces a fresh instance with empty refs. The visible
     symptom is a brief dialog-then-cancel flicker in dev. Production
     and dev-without-StrictMode behave correctly. The trade-off is
     accepted: the production-side hang prevention is high-value; the
     dev-mode flicker is bounded and degrades to the existing
     user-cancelled UI branch (no data loss). Distinguishing strict-
     mode replays from real unmounts without timing heuristics
     requires module-global instance tracking that would entangle this
     module-scoped queue with the per-instance lifecycle in ways the
     concurrency tests (Promise.all([a, b, c])) deliberately avoid. */
  React.useEffect(() => {
    return () => {
      const pending = activeRef.current;
      const queued = queueRef.current;
      activeRef.current = null;
      queueRef.current = [];
      if (pending) pending.resolve(false);
      for (const req of queued) req.resolve(false);
    };
  }, []);

  /* Stable per-request id so the description element + the Modal's
     aria-describedby reference each other across re-renders. Only
     set when the request actually has a body — otherwise there's
     nothing to describe and emitting ariaDescribedBy would point
     at an element that wouldn't render (codex Modal r1). */
  const bodyId = active?.body != null ? `jkit-confirm-body-${active.id}` : undefined;

  const node: ReactElement | null = active ? (
    <Modal
      key={active.id}
      open
      onClose={() => finish(active, false)}
      title={active.title}
      width={440}
      /* APG §alertdialog: confirmation dialogs that require explicit
         input use role="alertdialog" so AT signals them as
         interrupts (not as generic dialogs). Closes the PR #64 gap
         flagged in useConfirm spec §4.1 — this is the v5 fix. */
      role="alertdialog"
      ariaDescribedBy={bodyId}
    >
      <ConfirmBody
        id={bodyId}
        body={active.body}
        cancelLabel={active.cancelLabel ?? 'Cancel'}
        confirmLabel={active.confirmLabel ?? 'Confirm'}
        destructive={active.destructive}
        onConfirm={() => finish(active, true)}
        onCancel={() => finish(active, false)}
      />
    </Modal>
  ) : null;

  return { confirm, node };
}
